import { useGraphQLQuery } from '@betterment/graphql';
import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, Text } from '@betterment/samba-react';

import StepHeading from '../../AccountCreationFlow/components/StepHeading';
import { SecuritiesLendingStatus } from '../../graphql/schema';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import {
  SecLending,
  hasEnabledSecLending,
} from '../../Shared/components-v2/user-level-settings/components/SettingsControls';
import UserLevelSettings from '../../Shared/components-v2/user-level-settings/UserLevelSettings';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { DirectTradingFlowStateValues } from '../Controller';
import { DirectTradingAccountCreationFlowDocument } from '../graphql/query.directTradingAccountCreationFlow.generated';

export const UserLevelSettingsStep: React.FC<StepProps<DirectTradingFlowStateValues>> = ({
  goToNextStep,
  goToPreviousStep,
  flowState,
}) => {
  const navigate = useAutoNavigate();

  const { data } = useGraphQLQuery(DirectTradingAccountCreationFlowDocument, { throwOnError: true });

  if (!data) return <FullPageLoader />;

  const isEnrolled = data.userLevelSettings.securityLending.status !== SecuritiesLendingStatus.Unenrolled;
  const isFirstLegalAccount =
    data.userLevelSettings.securityLending.status === SecuritiesLendingStatus.LegalAccountRequired;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const hasSelectedSecurityLending = hasEnabledSecLending(event);
    if (isFirstLegalAccount && !hasSelectedSecurityLending) {
      goToNextStep({ isSecurityLendingEnrolled: null });
    } else {
      goToNextStep({ isSecurityLendingEnrolled: hasSelectedSecurityLending });
    }
  };

  return (
    <WorkflowLayout
      feature="add_direct_trading_account"
      pageTitle="Portfolio Controls"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
    >
      <Box css={{ display: 'grid', gap: 'space.150', marginBottom: 'space.300' }}>
        <StepHeading>Unlock more potential earnings from investing.</StepHeading>
        <Text variant="text100">
          Securities lending works across eligible accounts and can be disabled at any time.
        </Text>
      </Box>
      <form onSubmit={handleSubmit}>
        <UserLevelSettings controls={[SecLending(isEnrolled)]} />
        <Box
          css={{
            mt: 'space.100',
            smDown: {
              display: 'flex',
              position: 'fixed',
              bottom: 'space.0',
              left: 'space.0',
              right: 'space.0',
              justifyContent: 'center',
              backgroundColor: 'background.surface',
              paddingY: 'space.100',
              paddingX: 'space.300',
            },
          }}
        >
          <Button type="submit" variant="solidPrimary" css={{ smDown: { width: '100%' } }}>
            Continue
          </Button>
        </Box>
      </form>
    </WorkflowLayout>
  );
};
